/**
 * Supabase Configuration — SDK-free
 * All operations use direct REST API calls via fetch().
 * No @supabase/supabase-js dependency.
 */

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://buxwaufsryaxdwzlmcge.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJ1eHdhdWZzcnlheGR3emxtY2dlIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njc5NDcwOTksImV4cCI6MjA4MzUyMzA5OX0.78MEd556BjSBZ8NZJ8NhTroLVvGzr5ECTlrNKHkZ8n4';

const STORAGE_KEY = 'instacredmoney-auth';

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

/**
 * Read session from localStorage (no SDK needed).
 * Returns { data: { session }, error } to match the old interface.
 */
export const safeGetSession = async () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { data: { session: null }, error: null };

    const stored = JSON.parse(raw);
    const accessToken = stored.access_token || stored.session?.access_token;
    const refreshToken = stored.refresh_token || stored.session?.refresh_token;
    const user = stored.user || stored.session?.user || null;
    const expiresAt = stored.expires_at || stored.session?.expires_at || null;

    if (!accessToken) return { data: { session: null }, error: null };

    return {
      data: {
        session: { access_token: accessToken, refresh_token: refreshToken, user, expires_at: expiresAt }
      },
      error: null
    };
  } catch (error) {
    console.warn('safeGetSession error:', error);
    return { data: { session: null }, error };
  }
};

/**
 * Store session data to localStorage.
 */
export const storeSession = (sessionData) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
  } catch (e) {
    console.warn('Failed to store session:', e);
  }
};

/**
 * Clear session from localStorage.
 */
export const clearSession = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    // Clear any other Supabase-related keys
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('supabase') || key.includes('sb-'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (e) {
    console.warn('Failed to clear session:', e);
  }
};

/**
 * Refresh session using the Supabase REST API.
 * POST /auth/v1/token?grant_type=refresh_token
 * Returns true if session is valid/refreshed, false if user needs to re-login.
 */
export const refreshSession = async () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;

    const stored = JSON.parse(raw);
    const refreshToken = stored.refresh_token || stored.session?.refresh_token;
    const accessToken = stored.access_token || stored.session?.access_token;
    const expiresAt = stored.expires_at || stored.session?.expires_at;

    if (!accessToken) return false;

    // If token is not expiring soon (>5 min left), it's still valid
    if (expiresAt) {
      const expiresAtMs = expiresAt * 1000;
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      if (expiresAtMs - now > fiveMinutes) {
        return true; // Token still fresh
      }
    }

    // Token is expiring soon or we can't tell — try to refresh
    if (!refreshToken) {
      // No refresh token, but access token exists — trust it
      return !!accessToken;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const res = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
        method: 'POST',
        headers: {
          'apikey': supabaseAnonKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!res.ok) {
        console.warn('Token refresh failed:', res.status);
        // If we still have an access token, allow proceed
        return !!accessToken;
      }

      const data = await res.json();
      if (data.access_token) {
        // Store refreshed session
        storeSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_at: data.expires_at,
          expires_in: data.expires_in,
          token_type: data.token_type,
          user: data.user
        });
        console.log('Session refreshed successfully via REST API');
        return true;
      }

      return !!accessToken;
    } catch (err) {
      clearTimeout(timeoutId);
      console.warn('Token refresh error:', err.message);
      // On timeout/network error, trust existing token
      return !!accessToken;
    }
  } catch (e) {
    console.error('Error in refreshSession:', e);
    return false;
  }
};

export { supabaseUrl, supabaseAnonKey };
